import { TourConfig } from '../services/tour-guide.service';

export const SHIPMENT_MANAGEMENT_TOUR_CONFIG: TourConfig = {
  id: 'shipment-management-tour',
  name: 'Guida alla Gestione Spedizioni',
  steps: [
    {
      id: 'welcome',
      selector: '.main-title',
      title: '👋 Benvenuto nella Gestione Spedizioni',
      content: 'Questa guida ti mostrerà come utilizzare tutte le funzionalità per gestire e monitorare le tue spedizioni esistenti. Clicca "Avanti" per iniziare il tour.',
      position: 'bottom',
      showNext: true,
      showPrev: false,
      showSkip: true
    },
    {
      id: 'filters-section',
      selector: '#filters-section',
      title: '🔍 Sezione Filtri e Ricerca',
      content: 'Questa è la sezione principale per cercare e filtrare le spedizioni. Puoi utilizzare filtri base o avanzati per trovare rapidamente le spedizioni che ti interessano.',
      position: 'bottom',
      showNext: true,
      showPrev: true,
      showSkip: true
    },
    {
      id: 'basic-filters',
      selector: '#basic-filters',
      title: '🎯 Filtri di Base',
      content: 'Usa questi filtri per ricerche rapide: <strong>Codice Collo</strong>, <strong>Codice Bolla</strong>, <strong>Codice Dealer</strong> e <strong>Data</strong>. Sono i filtri più utilizzati per trovare spedizioni specifiche.',
      position: 'bottom',
      showNext: true,
      showPrev: true,
      showSkip: true,
      beforeShow: () => {
        // Assicurati che i filtri siano aperti
        const component = (window as any).shipmentManagementComponent;
        if (component && component.isFiltersCollapsed) {
          component.toggleFiltersCollapsed();
        }
      }
    },
    {
      id: 'date-filter',
      selector: 'input[placeholder*="data"]',
      title: '📅 Filtro Data Intelligente',
      content: 'Il filtro data è molto potente! Ha controlli di navigazione rapida (frecce) per spostarsi tra i giorni. Perfetto per controllare le spedizioni giorno per giorno.',
      position: 'top',
      showNext: true,
      showPrev: true,
      showSkip: true
    },
    {
      id: 'advanced-filters-toggle',
      selector: '.advanced-filters-toggle',
      title: '⚙️ Filtri Avanzati',
      content: 'Clicca qui per accedere ai <strong>filtri avanzati</strong>: intervallo di date, carrier specifici e altro. Utili per ricerche più complesse.',
      position: 'top',
      showNext: true,
      showPrev: true,
      showSkip: true
    },
    {
      id: 'advanced-filters-content',
      selector: '#advanced-filters',
      title: '🔧 Opzioni Filtri Avanzati',
      content: 'Qui trovi filtri per <strong>intervalli di date</strong> (Da/A), <strong>carrier specifici</strong> e <strong>clienti</strong>. Perfetti per analisi dettagliate.',
      position: 'bottom',
      showNext: true,
      showPrev: true,
      showSkip: true,
      beforeShow: () => {
        // Apri i filtri avanzati
        const component = (window as any).shipmentManagementComponent;
        if (component && component.isAdvancedFiltersCollapsed) {
          component.toggleAdvancedFilters();
        }
      }
    },
    {
      id: 'search-actions',
      selector: '.d-flex.gap-2:has(.btn-primary)',
      title: '🚀 Azioni di Ricerca',
      content: 'Usa <strong>"Reset Filtri"</strong> per pulire tutti i filtri, e <strong>"Cerca"</strong> per applicare i filtri impostati. Il pulsante cambia nome in "Ricerca Avanzata" quando i filtri avanzati sono aperti.',
      position: 'top',
      showNext: true,
      showPrev: true,
      showSkip: true
    },
    {
      id: 'summary-section',
      selector: '#summary-section',
      title: '📊 Sezione Resoconto',
      content: 'Questa è la sezione <strong>Resoconto</strong> che mostra statistiche in tempo reale sui dati filtrati: numero totale di bolle, colli, dealer, peso, volume e tipi di consegna.',
      position: 'top',
      showNext: true,
      showPrev: true,
      showSkip: true
    },
    {
      id: 'summary-cards',
      selector: '.summary-card:first-of-type',
      title: '📈 Carte Statistiche',
      content: 'Ogni carta mostra una <strong>statistica specifica</strong>: bolle totali, colli, dealer, peso complessivo, volume totale e tipi di instradamento. I valori si aggiornano automaticamente con i filtri!',
      position: 'bottom',
      showNext: true,
      showPrev: true,
      showSkip: true
    },
    {
      id: 'delivery-types-card',
      selector: '#delivery-types-card',
      title: '🚚 Carta Instradamenti',
      content: 'Questa carta mostra il <strong>numero totale di instradamenti</strong> (tipi di consegna) presenti nei dati filtrati. Il pulsante stampa in alto a destra permette di <strong>stampare un tour specifico</strong> scegliendo tra gli instradamenti disponibili.',
      position: 'bottom',
      showNext: true,
      showPrev: true,
      showSkip: true
    },
    {
      id: 'results-table',
      selector: '#results-table-title',
      title: '📊 Tabella Risultati',
      content: 'Questa tabella mostra tutte le <strong>bolle in sospeso</strong> e <strong>non inviate</strong>. È il cuore della gestione spedizioni dove puoi visualizzare e modificare le tue spedizioni.',
      position: 'right',
      showNext: true,
      showPrev: true,
      showSkip: true
    },
    {
      id: 'search-box',
      selector: '.custom-search-input',
      title: '🔎 Ricerca Rapida',
      content: 'Usa questa barra di ricerca per <strong>ricerche istantanee</strong> su tutti i dati visibili: codici, nomi dealer, descrizioni, ecc. La ricerca è in tempo reale!',
      position: 'bottom',
      showNext: true,
      showPrev: true,
      showSkip: true
    },
    {
      id: 'export-excel',
      selector: '.btn-excel',
      title: '📈 Export Excel',
      content: 'Esporta i dati filtrati in formato <strong>Excel</strong> per analisi offline. L\'export include tutti i dati visibili nella tabella corrente.',
      position: 'bottom',
      showNext: true,
      showPrev: true,
      showSkip: true
    },
    {
      id: 'column-filters-button',
      selector: '.btn:has(.fa-filter)',
      title: '🎛️ Pulsante Filtri Colonna',
      content: 'Clicca questo pulsante per attivare i <strong>filtri avanzati per ogni colonna</strong> della tabella. Ora li apriremo per mostrarteli!',
      position: 'bottom',
      showNext: true,
      showPrev: true,
      showSkip: true
    },
    {
      id: 'column-filters',
      selector: '#column-filters-section',
      title: '🎛️ Filtri Colonna Attivi',
      content: 'Questi sono i <strong>filtri avanzati per ogni colonna</strong> della tabella. Ideali per <strong>filtraggio preciso</strong> su campi specifici come codici, stati, date, ecc. Ogni campo ha il suo filtro dedicato!',
      position: 'bottom',
      showNext: true,
      showPrev: true,
      showSkip: true,
      beforeShow: () => {
        // Apri i filtri colonna se non sono già aperti
        const component = (window as any).shipmentManagementComponent;
        if (component && !component.isColumnFiltersVisible) {
          component.toggleColumnFilters();
        }
      }
    },
    {
      id: 'view-toggle',
      selector: '.btn-group:has(.fa-file-alt, .fa-cube)',
      title: '👁️ Modalità Visualizzazione',
      content: 'Cambia tra visualizzazione per <strong>"Bolle"</strong> (lettere di vettura) e <strong>"Colli"</strong> (singoli pacchi). Ogni modalità mostra informazioni diverse e specifiche.',
      position: 'bottom',
      showNext: true,
      showPrev: true,
      showSkip: true
    },
    {
      id: 'pagination-controls',
      selector: '.pagination-selector',
      title: '📄 Controllo Paginazione',
      content: 'Seleziona quanti elementi visualizzare per pagina: 5, 10, 25, 50 o 100. Più elementi = meno pagine, ma caricamento più lento.',
      position: 'top',
      showNext: true,
      showPrev: true,
      showSkip: true
    },
    {
      id: 'counter-badge',
      selector: '.counter-display',
      title: '📊 Contatore Risultati',
      content: 'Mostra il numero totale di <strong>bolle</strong> o <strong>colli</strong> trovati con i filtri correnti. Utile per avere una panoramica rapida.',
      position: 'left',
      showNext: true,
      showPrev: true,
      showSkip: true
    },
    {
      id: 'table-content',
      selector: '.table-responsive',
      title: '📋 Contenuto Tabella',
      content: 'Ogni riga rappresenta una bolla o un collo. Puoi cliccare sulle righe per vedere i dettagli e usare i pulsanti di azione per modificare lo stato delle spedizioni.',
      position: 'top',
      showNext: true,
      showPrev: true,
      showSkip: true
    },
    {
      id: 'status-indicators',
      selector: '.status-indicator:first-of-type',
      title: '🏷️ Indicatori di Stato',
      content: 'I <strong>badge colorati</strong> mostrano lo stato delle spedizioni: <strong>PENDING</strong> (in attesa), <strong>ACTIVE</strong> (attivo), <strong>UNSENT</strong> (non inviato), ecc. Ogni colore ha un significato specifico per identificare rapidamente lo stato.',
      position: 'top',
      showNext: true,
      showPrev: true,
      showSkip: true
    },
    {
      id: 'download-waybill',
      selector: '.download-btn:first-of-type',
      title: '📄 Download Bolle PDF',
      content: 'Clicca questo pulsante per <strong>scaricare il PDF</strong> della lettera di vettura. Il documento contiene tutti i dettagli della spedizione ed è utile per stampe o invii via email. L\'icona rossa indica file PDF.',
      position: 'top',
      showNext: true,
      showPrev: true,
      showSkip: true
    },
    {
      id: 'action-buttons',
      selector: '.action-button:first-of-type',
      title: '⚡ Pulsanti di Azione',
      content: 'Usa questi pulsanti per <strong>selezionare bolle</strong>, <strong>assegnare corrieri</strong> o <strong>cambiare stato</strong> delle spedizioni. Il pulsante cambia colore e testo in base allo stato: verde per "Ultimata", blu per "Seleziona".',
      position: 'top',
      showNext: true,
      showPrev: true,
      showSkip: true
    },
    {
      id: 'tips-workflow',
      selector: '.main-title',
      title: '💡 Consigli per il Flusso di Lavoro',
      content: '<strong>Flusso consigliato:</strong><br/>1️⃣ Filtra per data o codice<br/>2️⃣ Cerca elementi specifici<br/>3️⃣ Cambia visualizzazione se necessario<br/>4️⃣ Usa azioni per gestire gli stati<br/>5️⃣ Esporta se serve analisi offline',
      position: 'bottom',
      showNext: true,
      showPrev: true,
      showSkip: true
    },
    
    {
      id: 'tour-complete',
      selector: '.main-title',
      title: '🎉 Tour Completato!',
      content: 'Perfetto! Ora conosci tutte le funzionalità della <strong>Gestione Spedizioni</strong>. Usa questo tour quando vuoi ripassare le funzionalità. Buon lavoro! 🚀',
      position: 'bottom',
      showNext: false,
      showPrev: true,
      showSkip: false
    }
  ]
};
